<template>
  <!-- 流程设置：上方是流程定义列表，点击某条在下方画布中编排 -->
  <div class="process-setting-detail">
    <list-page>
      <list-table class="flow-list-table">
        <yq-table :data="processList" height="100%" stripe border highlight-current-row
                  :current-row-key="currentProcess ? currentProcess.id : ''"
                  row-key="id" @rowClick="onRowClick">
          <yq-table-column label="流程名称" prop="processName" min-width="180rem"/>
          <yq-table-column label="版本" prop="defVersion" width="60rem"/>
          <yq-table-column label="状态" prop="defStatus" width="120rem">
            <template v-slot="{row}">
              <el-tag v-if="row.defStatus === 0" type="info">草稿</el-tag>
              <el-tag v-else-if="row.defStatus === 100" type="success">已发布</el-tag>
              <el-tag v-else type="warning">已停用</el-tag>
            </template>
          </yq-table-column>
          <yq-table-column label="版本说明" prop="remark" min-width="120rem"/>
          <yq-table-column label="操作" prop="options" :align="'center'" fixed="right" width="180rem" :show-overflow-tooltip="false">
            <template #header>
              <table-button has-perm="process:setting:save" @click="addProcess">
                <yq-icon name="element-ui_plus"/> 新建流程
              </table-button>
            </template>
            <template v-slot="{row}">
              <table-button-group label="更多操作">
                <table-button has-perm="process:setting:save" :disabled="row.defStatus === 100"
                              @click="publishProcess(row)">发布</table-button>
                <!-- 已发布的版本不可修改，复制一份草稿再改是标准的改版方式 -->
                <table-button has-perm="process:setting:save" @click="copyProcess(row)">复制</table-button>
                <table-button has-perm="process:setting:delete" el-type="danger"
                              message="是否确认删除该流程定义" @click="deleteProcess(row)">删除</table-button>
              </table-button-group>
            </template>
          </yq-table-column>
        </yq-table>
      </list-table>

      <form-tag :label="currentProcess ? `流程编排：${currentProcess.processName}（双击节点可配置）` : '流程编排：请先在上方选择一条流程'"/>

      <list-table class="flow-canvas-table" background-color="#FFF">
        <div class="canvas-wrap">
          <logic-flow ref="logicFlow" :def-json="currentDefJson" :business-type="businessType"
                      :flow-id="currentProcess ? currentProcess.id : ''"
                      :readonly="!currentProcess" @change="onGraphChange"/>
          <!-- 未选中流程时遮罩，明确告知不可操作，而不是让人对着画布猜测 -->
          <div class="canvas-mask" v-if="!currentProcess">
            <yq-icon name="element-ui_info"/>
            <span>请先在上方选择一条流程</span>
          </div>
        </div>
      </list-table>

      <form-page :show-page="false">
        <page-button has-perm="process:setting:save" type="write" el-type="primary"
                     :disabled="!currentProcess" @click="saveGraph">保存流程</page-button>
        <page-button @click="router.go(-1)">返回</page-button>
      </form-page>
    </list-page>

    <!-- 新建流程 = 新增版本 -->
    <el-dialog dialog-width="1" title="新增流程版本" v-model="showAddDialog" :close-on-click-modal="false">
      <form-items :columns="1" :data="addForm" ref="addFormRef" label-width="2.4rem">
        <form-item label="流程名称" name="processName" :required="true" error-message="请输入流程名称">
          <el-input v-model="addForm.processName" placeholder="如 订单变更审批"/>
        </form-item>
        <form-item label="版本号">
          <el-input :model-value="addForm.defVersion + '（预计）'" disabled/>
          <template #append>
            <el-tooltip content="版本号由后台按该业务的历史最大版本递增（含已删除的版本），保存后以实际写入为准">
              <yq-icon name="element-ui_info" style="color: var(--yq-font-color-desc)"/>
            </el-tooltip>
          </template>
        </form-item>
        <form-item label="版本说明">
          <el-input v-model="addForm.remark" placeholder="本次版本改了什么"/>
        </form-item>
      </form-items>
      <div slot="footer">
        <page-button @click="showAddDialog = false">取消</page-button>
        <page-button type="write" el-type="primary" @click="saveNewProcess">保存</page-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import ListPage from "@/components/h5/layout/ListPage";
import ListTable from "@/components/h5/layout/ListTable.vue";
import FormPage from "@/components/h5/layout/FormPage.vue";
import FormTag from "@/components/h5/layout/FormTag.vue";
import DialogTable from "@/components/h5/layout/DialogTable.vue";
import FormItems from "@/components/h5/layout/FormItems.vue";
import FormItem from "@/components/h5/layout/FormItem.vue";
import PageButton from "@/components/h5/button/PageButton.vue";
import TableButton from "@/components/h5/button/TableButton";
import TableButtonGroup from "@/components/h5/button/TableButtonGroup.vue";
import YqTable from "@/components/h5/table/YqTable.vue";
import YqTableColumn from "@/components/h5/table/YqTableColumn.vue";
import YqIcon from "@/components/YqIcon.vue";
import LogicFlow from "@/views/h5/common/sys/process/logicFlow/LogicFlow.vue";
import Util from "@/util/util";
import {useRoute, useRouter} from "vue-router";
import {ElMessageBox} from "element-plus";
import processSettingApi from "@/api/normal/sys/processSettingApi";

export default {
  setup() {
    return {
      router: useRouter(),
      route: useRoute(),
    }
  },
  name: "ProcessSettingDetail",
  components: {
    YqIcon, YqTable, LogicFlow, PageButton, DialogTable, FormItems, FormItem, FormTag,
    ListTable, YqTableColumn, TableButtonGroup, TableButton, ListPage, FormPage
  },
  data() {
    return {
      businessType: "",
      processList: [],
      currentProcess: null,
      currentDefJson: "",
      showAddDialog: false,
      addForm: { processName: "", defVersion: 1, remark: "" },
    };
  },
  beforeRouteLeave(to, from, next) {
    // 只缓存不加载：编辑态数据不适合回填，每次进来重新拉取
    Util.cachePageData(this, to, from);
    next();
  },
  created() {
    this.businessType = this.route.query.businessType || "";
    this.queryProcessList();
  },
  methods: {
    queryProcessList() {
      processSettingApi.queryProcessList(this.businessType).then(list => {
        this.processList = list || [];
      });
    },
    // 点击某条流程，把它的 defJson 交给画布渲染
    onRowClick(row) {
      this.currentProcess = row;
      this.currentDefJson = row.defJson || "";
    },
    onGraphChange(graph) {
      // 画布每次变更都同步回 currentDefJson，保存时直接用
      this.currentDefJson = JSON.stringify(graph);
    },
    saveGraph() {
      if (!this.currentProcess) {
        this.$message.error("请先选择一条流程");
        return;
      }
      // 画布有改动时以画布为准；没有改动时保留原 defJson，避免把空图覆盖上去
      let defJson = this.currentDefJson;
      if (!defJson) {
        this.$message.error("流程图内容为空，无法保存");
        return;
      }
      const params = {
        ...this.currentProcess,
        defJson: defJson,
      };
      processSettingApi.saveProcess(params).then(res => {
        this.currentProcess = res;
        this.currentDefJson = res.defJson || "";
        this.queryProcessList();
      });
    },
    /**
     * 新建流程 = 新增一个版本。
     * 一个业务一个流程（business_type 关联），改版时 def_version 递增。
     * 不允许多个业务共用一个流程：流程编排时条件编辑器要按业务加载可用字段，
     * 共享流程会导致字段来源不明确、拿不到业务数据（如按订单金额分流审批人）。
     */
    addProcess() {
      this.addForm = {
        processName: "",
        defVersion: this.buildNextVersion(),
        remark: ""
      };
      this.showAddDialog = true;
    },
    /** 版本号在已有最大版本上递增 */
    buildNextVersion() {
      const list = this.processList || [];
      if (list.length === 0) {
        return 1;
      }
      return Math.max(...list.map(p => p.defVersion || 1)) + 1;
    },
    saveNewProcess() {
      if (!this.$refs.addFormRef.validCheck()) {
        return;
      }
      const params = {
        ...this.addForm,
        // business_type 是流程与业务配置的关联键，后端据此匹配
        businessType: this.businessType,
        // 新建时给一张空图，避免 defJson 为空导致后端校验不过
        defJson: JSON.stringify({ nodes: [], edges: [] }),
        defStatus: 0,
      };
      processSettingApi.saveProcess(params).then(res => {
        this.showAddDialog = false;
        this.queryProcessList();
        this.currentProcess = res;
        this.currentDefJson = res.defJson || "";
      });
    },
    publishProcess(row) {
      // 发布后的流程会被在途实例绑定，等同于数据变更不可逆，先做完整性校验再二次确认
      const errors = this.checkGraphValid(row.defJson);
      if (errors.length > 0) {
        ElMessageBox.alert(errors.join("；"), "流程校验未通过", {
          type: "warning",
          confirmButtonText: "知道了"
        }).catch(() => {
        });
        return;
      }
      ElMessageBox.confirm("流程发布后将被在途实例绑定，不可再修改本版本。是否确认发布？", "发布确认", {
        type: "warning",
        confirmButtonText: "确认发布",
        cancelButtonText: "取消"
      }).then(() => {
        processSettingApi.publishProcess(row.id).then(() => {
          this.queryProcessList();
        });
      }).catch(() => {
      });
    },
    /**
     * 流程定义完整性校验。
     * 返回问题列表，空数组表示通过。
     * 校验项对应设计文档的要求：必须有起止节点、节点配置完整、条件分支有 default。
     */
    checkGraphValid(defJson) {
      const errors = [];
      let graph;
      try {
        graph = JSON.parse(defJson || "{}");
      } catch (e) {
        return ["流程定义内容不是合法的JSON"];
      }
      const nodes = graph.nodes || [];
      const edges = graph.edges || [];
      if (nodes.length === 0) {
        return ["流程图内容为空，请先编排流程"];
      }
      // 1. 必须有且仅有一个开始节点、一个结束节点
      const startNodes = nodes.filter(n => n.properties && n.properties.nodeType === "start");
      const endNodes = nodes.filter(n => n.properties && n.properties.nodeType === "end");
      if (startNodes.length === 0) {
        errors.push("缺少开始节点");
      } else if (startNodes.length > 1) {
        errors.push("存在多个开始节点");
      }
      if (endNodes.length === 0) {
        errors.push("缺少结束节点");
      }
      // 2. 业务节点必须选定要回调的业务节点，否则引擎不知道回调哪个接口
      nodes.forEach(node => {
        const props = node.properties || {};
        if (props.nodeType !== "business") {
          return;
        }
        const nodeName = props.nodeName || "未命名业务节点";
        let params = {};
        if (props.businessParams) {
          try {
            params = JSON.parse(props.businessParams);
          } catch (e) {
            errors.push(`业务节点【${nodeName}】的配置内容不是合法JSON`);
            return;
          }
        }
        if (!params.nodeId) {
          errors.push(`业务节点【${nodeName}】未选择业务节点，请先在「业务设置」中配置`);
        }
      });
      // 3. 审批节点必须配置完整的审批人信息
      nodes.forEach(node => {
        const props = node.properties || {};
        if (props.nodeType !== "approve") {
          return;
        }
        const nodeName = props.nodeName || "未命名审批节点";
        let params = {};
        if (props.businessParams) {
          try {
            params = JSON.parse(props.businessParams);
          } catch (e) {
            errors.push(`审批节点【${nodeName}】的配置内容不是合法JSON`);
            return;
          }
        }
        if (!params.checkType) {
          errors.push(`审批节点【${nodeName}】未设置签署方式`);
          return;
        }
        // 或签/会签都需要人；自动通过(checkType=3)不需要
        if (params.checkType === 1 || params.checkType === 2) {
          if (!params.employeeType) {
            errors.push(`审批节点【${nodeName}】未指定签署人员类型`);
          } else if (params.employeeType === 1 && !params.employeeRange) {
            errors.push(`审批节点【${nodeName}】未选择签署人员`);
          }
        }
      });
      // 3. 非结束节点必须有出线，否则流程会走到死胡同
      nodes.forEach(node => {
        const props = node.properties || {};
        if (props.nodeType === "end") {
          return;
        }
        const hasOut = edges.some(e => e.sourceNodeId === node.id);
        if (!hasOut) {
          errors.push(`节点【${props.nodeName || props.nodeType}】没有后续连线，流程会中断`);
        }
      });
      // 4. 条件分支必须有一个 default 兜底分支，否则条件不覆盖时流程卡死
      nodes.forEach(node => {
        const props = node.properties || {};
        if (props.nodeType !== "condition") {
          return;
        }
        const outEdges = edges.filter(e => e.sourceNodeId === node.id);
        if (outEdges.length === 0) {
          return;
        }
        const hasDefault = outEdges.some(e => {
          const conditionList = (e.properties && e.properties.conditionList) || [];
          return conditionList.length === 0;
        });
        if (!hasDefault) {
          errors.push("条件分支【" + (props.nodeName || node.id) + "】缺少默认分支(default)，条件不覆盖时流程会卡死");
        }
      });
      return errors;
    },
    /**
     * 复制流程为新的草稿版本。
     * 已发布的版本不可修改，改版的正确做法是复制一份草稿再改——
     * 在途实例仍绑定旧版本，新版本发布后新发起的流程才走新版。
     *
     * 实现上不新增后端接口：清空 id 与版本号后按「新增」提交即可，
     * saveProcess 依据有无 id 判断新增/修改，会自动分配下一个版本号。
     */
    copyProcess(row) {
      ElMessageBox.confirm(
        `将复制【${row.processName}】为一个新的草稿版本，是否继续？`,
        "复制流程",
        {type: "info", confirmButtonText: "确认复制", cancelButtonText: "取消"}
      ).then(() => {
        const params = {
          ...row,
          // 清掉标识与版本，交由后端按新增处理并分配新版本号
          id: "",
          defVersion: null,
          // 复制出来的必须是草稿，不能继承已发布状态
          defStatus: 0,
          // 保留流程图内容，这正是复制的意义
          defJson: row.defJson || JSON.stringify({nodes: [], edges: []}),
          remark: row.remark ? `复制自 v${row.defVersion}：${row.remark}` : `复制自 v${row.defVersion}`,
        };
        // 主键、创建信息等由后端重新生成，避免带上旧记录的值
        delete params.createdTime;
        delete params.createdId;
        delete params.updatedTime;
        delete params.updatedId;
        processSettingApi.saveProcess(params).then(res => {
          this.queryProcessList();
          // 直接选中新版本，方便马上修改
          this.onRowClick(res);
        });
      }).catch(() => {
      });
    },
    deleteProcess(row) {
      processSettingApi.deleteProcess(row.id).then(() => {
        if (this.currentProcess && this.currentProcess.id === row.id) {
          this.currentProcess = null;
          this.currentDefJson = "";
        }
        this.queryProcessList();
      });
    },
  }
};
</script>

<style lang="scss" scoped>
.process-setting-detail {
  height: 100%;

  :deep(.flow-list-table) {
    // 上方列表占 1/3，下方画布占 2/3
    flex: 1;
    min-height: 0;
  }

  :deep(.flow-canvas-table) {
    flex: 2;
    min-height: 0;
  }

  // 被选中的流程行加背景色标识。
  // el-table 默认的高亮是浅灰，在白色背景下几乎看不出来，
  // 这里换成带主色调的浅蓝并加左侧色条，明确「当前正在编辑哪一条」。
  :deep(.flow-list-table .el-table__body tr.current-row > td.el-table__cell) {
    background-color: #ecf5ff;
  }

  :deep(.flow-list-table .el-table__body tr.current-row > td.el-table__cell:first-child) {
    box-shadow: inset 3px 0 0 var(--el-color-primary);
  }

  // 鼠标悬停时与选中态区分开，避免混淆
  :deep(.flow-list-table .el-table__body tr:hover > td.el-table__cell) {
    background-color: #f5f7fa;
  }

  :deep(.flow-list-table .el-table__body tr.current-row:hover > td.el-table__cell) {
    background-color: #ecf5ff;
  }

  .canvas-wrap {
    position: relative;
    height: 100%;
  }

  // 未选中流程时盖住画布，明确告知不可操作
  .canvas-mask {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    background: rgba(255, 255, 255, 0.72);
    color: var(--yq-font-color-desc);
    font-size: 14px;
  }
}
</style>
